import React from "react";
import { MoreHorizontal, StickyNote, AlertTriangle, Pencil, Copy, Link2, Send, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  STATUS_LABELS, STATUS_STYLES, STATUS_BAR, PRIMARY_ACTION, SECONDARY_ACTIONS, RESTORE_ACTION, RESTORABLE,
} from "./agendaConfig";

function ActionsMenu({ r, perms, on }) {
  const secondary = perms.canEdit ? (SECONDARY_ACTIONS[r.status] || [])
    .filter((a) => perms.canCancel || a.to !== "annullata") : [];
  const canRestore = perms.canEdit && RESTORABLE.includes(r.status)
    && (perms.canCancel || r.status !== "annullata");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} aria-label="Altre azioni"
                data-testid={`agenda-menu-${r.id}`}
                className="w-11 h-11 rounded-full border border-sand-300 bg-white flex items-center justify-center
                           hover:border-bronze-400 transition-colors">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" onClick={(e) => e.stopPropagation()}
                           data-testid={`agenda-menu-content-${r.id}`}>
        <DropdownMenuItem onSelect={() => on.open(r)} data-testid={`agenda-menu-open-${r.id}`}>
          <Pencil className="w-4 h-4 mr-2" /> {perms.canEdit ? "Modifica / Sposta" : "Dettagli"}
        </DropdownMenuItem>
        {perms.canCreate && (
          <DropdownMenuItem onSelect={() => on.duplicate(r)} data-testid={`agenda-menu-duplicate-${r.id}`}>
            <Copy className="w-4 h-4 mr-2" /> Duplica
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={() => on.copyLink(r)} data-testid={`agenda-menu-link-${r.id}`}>
          <Link2 className="w-4 h-4 mr-2" /> Copia link conferma
        </DropdownMenuItem>
        {perms.canEdit && (
          <DropdownMenuItem onSelect={() => on.resend(r)} disabled={!perms.waAvailable} data-testid={`agenda-menu-resend-${r.id}`}>
            <Send className="w-4 h-4 mr-2" /> {perms.waAvailable ? "Rinvia messaggio" : "Rinvia (dopo attivazione WhatsApp)"}
          </DropdownMenuItem>
        )}
        {(secondary.length > 0 || canRestore) && <DropdownMenuSeparator />}
        {secondary.map(({ to, label, Icon }) => (
          <DropdownMenuItem key={to} onSelect={() => on.status(r, to)} className="text-rose-700 focus:text-rose-800"
                            data-testid={`agenda-menu-status-${to}-${r.id}`}>
            <Icon className="w-4 h-4 mr-2" /> {label}
          </DropdownMenuItem>
        ))}
        {canRestore && (
          <DropdownMenuItem onSelect={() => on.status(r, RESTORE_ACTION.to, { force: true, confirm: true })}
                            data-testid={`agenda-menu-restore-${r.id}`}>
            <RESTORE_ACTION.Icon className="w-4 h-4 mr-2" /> {RESTORE_ACTION.label}
          </DropdownMenuItem>
        )}
        {perms.canDelete && <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => on.remove(r)} className="text-rose-700 focus:text-rose-800"
                            data-testid={`agenda-menu-delete-${r.id}`}>
            <Trash2 className="w-4 h-4 mr-2" /> Elimina definitivamente
          </DropdownMenuItem>
        </>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AgendaRow({ r, perms, on, index }) {
  const primary = perms.canEdit ? PRIMARY_ACTION[r.status] : null;
  const hasNotes = Boolean(r.notes || r.occasion);
  const allergies = r.allergies || [];
  const place = [r.zone, r.table_number ? `Tav. ${r.table_number}` : null].filter(Boolean).join(" · ");
  const dimmed = ["annullata", "no_show", "completata"].includes(r.status);
  return (
    <li style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
        className={`agenda-row group relative flex items-center gap-3 sm:gap-4 rounded-2xl border border-sand-200 bg-white
                    pl-4 pr-2 sm:pr-3 py-3 cursor-pointer hover:border-bronze-300 hover:shadow-sm
                    transition-[border-color,box-shadow] ${dimmed ? "opacity-60" : ""}`}
        onClick={() => on.open(r)} data-testid={`agenda-row-${r.id}`}>
      <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${STATUS_BAR[r.status]}`} />
      <div className="w-14 sm:w-16 shrink-0">
        <div className="font-data text-xl sm:text-[22px] font-semibold leading-none text-ink-900"
             data-testid={`agenda-time-${r.id}`}>{r.time}</div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span lang="it" className="line-clamp-2 [overflow-wrap:anywhere] hyphens-auto sm:truncate text-[15px] sm:text-base font-medium text-ink-900 leading-snug" data-testid={`agenda-name-${r.id}`}>
            {r.customer_name} {r.customer_surname || ""}
          </span>
          {hasNotes && <StickyNote className="w-4 h-4 text-bronze-600 shrink-0" aria-label="Note presenti"
                                   data-testid={`agenda-notes-flag-${r.id}`} />}
          {allergies.length > 0 && <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" aria-label="Allergie"
                                                  data-testid={`agenda-allergy-flag-${r.id}`} />}
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-ink-500 min-w-0">
          <span className={`shrink-0 rounded-full border px-2 py-0.5 ${STATUS_STYLES[r.status]}`}
                data-testid={`agenda-status-${r.id}`}>{STATUS_LABELS[r.status]}</span>
          {place && <span className={`truncate ${r.zone === "Sabbia" ? "text-amber-800 font-semibold" : ""}`} data-testid={`agenda-place-${r.id}`}>{place}</span>}
        </div>
      </div>
      <div className="shrink-0 text-right pr-1">
        <div className="font-data text-xl font-semibold text-ink-900 leading-none"
             data-testid={`agenda-pax-${r.id}`}>{r.pax}</div>
        <div className="text-[11px] uppercase tracking-[0.14em] text-ink-500 mt-1">pax</div>
      </div>
      {primary && (
        <button type="button" onClick={(e) => { e.stopPropagation(); on.status(r, primary.to); }}
                data-testid={`agenda-primary-${r.id}`}
                className="hidden sm:inline-flex h-11 px-4 rounded-full bg-ink-900 text-white text-sm items-center gap-1.5
                           hover:bg-ink-700 active:scale-[0.98] transition-[background-color,transform]">
          <primary.Icon className="w-4 h-4" /> {primary.label}
        </button>
      )}
      {primary && (
        <button type="button" onClick={(e) => { e.stopPropagation(); on.status(r, primary.to); }}
                aria-label={primary.label} data-testid={`agenda-primary-mobile-${r.id}`}
                className="sm:hidden w-11 h-11 rounded-full bg-ink-900 text-white flex items-center justify-center active:scale-95">
          <primary.Icon className="w-4 h-4" />
        </button>
      )}
      <ActionsMenu r={r} perms={perms} on={on} />
    </li>
  );
}
